import { FC } from 'react'
import { useLocation } from 'react-router-dom'

import { Button, Variants as ButtonVariants } from '@factorialco/deprecated-design-system'
import {
  BulkUpdateTasksDocument,
  TasksValueObjectsTaskIdentifierEnum,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RefetchQuery } from 'modules/tasks/hooks/useBulkUpdateTasks'
import { taskStatuses } from 'modules/tasks/lib/status'

interface Props {
  type?: ButtonVariants
  taskIds: number[]
  markAsDone?: boolean
  disabled?: boolean
  identifier: TasksValueObjectsTaskIdentifierEnum
  refetchQueries?: DocumentNode[] | RefetchQuery[]
}

const CompleteTask: FC<Props> = ({
  type = 'secondary',
  taskIds,
  identifier,
  disabled,
  markAsDone,
  refetchQueries,
}) => {
  const { pathname } = useLocation()
  const [bulkUpdateTasks, { loading: bulkUpdateTasksLoading }] = useMutation(
    BulkUpdateTasksDocument,
    {
      extractorFn: (data) => data.tasksMutations.bulkUpdateTasksTask,
    }
  )

  const pathPage = () => {
    if (pathname.includes('my-projects')) return 'my projects employee view'
    if (pathname.includes('contributors')) return 'my projects manager view'
    return 'project tasks'
  }

  const trackEvent = (status: TasksValueObjectsTaskStatusEnum) => {
    if (identifier === 'project') {
      track(trackingEvents.projectManagement.tasks.updateTaskStatus, {
        mark_as: `Mark as ${status}`,
        number: taskIds.length,
        identifier: identifier,
        path_page: pathPage(),
        origin: 'bulk_update',
      })
    }
  }

  const onSelectorChange = async (status: TasksValueObjectsTaskStatusEnum) => {
    const tasks = taskIds.map((id) => ({
      id,
      status,
      identifier,
    }))

    trackEvent(status)
    const result = await bulkUpdateTasks({
      variables: { tasks },
      refetchQueries,
    })

    const handled = handleResult(result, (_) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  const primaryLabel = markAsDone ? i18n.t('tasks.mark.done') : i18n.t('tasks.mark.undone')

  if (!taskIds.length) return null

  return (
    <>
      <Button.Split
        label={primaryLabel}
        type={type}
        onClick={() => onSelectorChange(markAsDone ? 'done' : 'todo')}
        loading={bulkUpdateTasksLoading}
        disabled={disabled || bulkUpdateTasksLoading}
        optionsDisabled={disabled}
      >
        {taskStatuses().map((option) => (
          <Button.Split.Option
            disabled={disabled}
            key={option.title}
            onClick={() => onSelectorChange(option.status)}
          >
            {option.title}
          </Button.Split.Option>
        ))}
      </Button.Split>
    </>
  )
}

export default CompleteTask
